import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { formatCurrency } from '../lib/utils';
import { Frequency, RecurringTransaction } from '../types';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { DatePicker } from './ui/datepicker';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

interface RecurringTransactionsProps {
  transactions: RecurringTransaction[];
  onTransactionsChange: (transactions: RecurringTransaction[]) => void;
}

function today() {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}

function RecurringTransactions({ transactions, onTransactionsChange }: RecurringTransactionsProps) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState<Frequency>('monthly');
  const [startDate, setStartDate] = useState<Date>(today());

  const handleAdd = () => {
    if (!name || !amount) return;

    const newTransaction: RecurringTransaction = {
      id: Date.now(),
      name,
      amount: Number(amount),
      frequency,
      startDate,
    };

    onTransactionsChange([...transactions, newTransaction]);
    setName('');
    setAmount('');
    setIsFormOpen(false);
  };

  const handleDelete = (id: number) => {
    onTransactionsChange(transactions.filter(t => t.id !== id));
  };

  const startEditing = (transaction: RecurringTransaction) => {
    setEditingId(transaction.id);
    setName(transaction.name);
    setAmount(transaction.amount.toString());
    setFrequency(transaction.frequency);
    setStartDate(transaction.startDate);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setName('');
    setAmount('');
    setFrequency('monthly');
    setStartDate(new Date());
  };

  const saveEdit = () => {
    if (!editingId || !name || !amount) return;

    onTransactionsChange(transactions.map(t =>
      t.id === editingId
        ? { ...t, name, amount: Number(amount), frequency, startDate }
        : t
    ));

    cancelEditing();
  };

  const renderRow = (transaction: RecurringTransaction) => {
    const isEditing = transaction.id === editingId;

    if (isEditing) {
      return (
        <tr key={transaction.id} className="border-b border-border/50">
          <td className="py-2 pr-4">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name"
            />
          </td>
          <td className="py-2 pr-4">
            <div className="relative">
              <span className="absolute left-3 top-2.5">$</span>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-6"
                placeholder="0.00"
              />
            </div>
          </td>
          <td className="py-2 pr-4">
            <Select value={frequency} onValueChange={(value: Frequency) => setFrequency(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="biweekly">Biweekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </td>
          <td className="py-2 pr-4">
            <DatePicker
              date={startDate}
              onChange={(date) => date && setStartDate(date)}
            />
          </td>
          <td className="py-2 text-right space-x-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={saveEdit}
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={cancelEditing}
            >
              <X className="h-4 w-4" />
            </Button>
          </td>
        </tr>
      );
    }

    return (
      <tr
        key={transaction.id}
        className="border-b border-border/50 last:border-0"
      >
        <td className="py-2 pr-4">
          {transaction.name}
        </td>
        <td className="py-2 pr-4">
          <span className={transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
            {transaction.amount >= 0 ? '+' : '-'}{formatCurrency(transaction.amount)}
          </span>
        </td>
        <td className="py-2 pr-4 capitalize">
          <Badge variant="outline">{transaction.frequency}</Badge>
        </td>
        <td className="py-2 pr-4">
          {transaction.startDate.toLocaleDateString()}
        </td>
        <td className="py-2 text-right space-x-1 flex">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => startEditing(transaction)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => handleDelete(transaction.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2 font-medium">Name</th>
            <th className="text-left py-2 font-medium">Amount</th>
            <th className="text-left py-2 font-medium">Frequency</th>
            <th className="text-left py-2 font-medium">Start Date</th>
            <th className="text-right py-2 font-medium w-[50px]">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setIsFormOpen(!isFormOpen)}
              >
                <Plus className={`h-4 w-4 transition-transform ${isFormOpen ? 'rotate-45' : ''}`} />
              </Button>
            </th>
          </tr>
        </thead>
        <tbody>
          {isFormOpen && (
            <tr className="border-b border-border/50">
              <td className="py-2 pr-4">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                />
              </td>
              <td className="py-2 pr-4">
                <div className="relative">
                  <span className="absolute left-3 top-2.5">$</span>
                  <Input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="pl-6"
                    placeholder="0.00"
                  />
                </div>
              </td>
              <td className="py-2 pr-4">
                <Select value={frequency} onValueChange={(value: Frequency) => setFrequency(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Biweekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </td>
              <td className="py-2 pr-4">
                <DatePicker
                  date={startDate}
                  onChange={(date) => date && setStartDate(date)}
                />
              </td>
              <td className="py-2 text-right">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={handleAdd}
                >
                  <Check className="h-4 w-4" />
                </Button>
              </td>
            </tr>
          )}
          {transactions.map(renderRow)}
          {transactions.length === 0 && !isFormOpen && (
            <tr>
              <td colSpan={5} className="py-4 text-center text-sm text-muted-foreground">
                No recurring transactions yet
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default RecurringTransactions; 